import mapboxgl from 'mapbox-gl';
import { hexToRgbArray } from '@shared/helpers/color.helpers';
import type { Coordinate } from '@shared/models/geography.model';
import type { Shape } from '@shared/models/design/storey.model';
import { canvasColors, mapRenderingConstants } from '../map.constants';
import { getShapeRingCoordinates } from '../helpers/map.helpers';
import type { PointData } from './types';
import { RIGHT_ANGLE_INDICATOR_TOLERANCE_DEGREES } from '../helpers/corner-angle-edit.helpers';

/** Fixed arm length in CSS pixels (constant on screen at every zoom). */
const ARM_SIZE_PX = 16;

/** Angle tolerance in radians (±0.5°) for treating a corner as 90° */
const RIGHT_ANGLE_TOLERANCE =
  RIGHT_ANGLE_INDICATOR_TOLERANCE_DEGREES * (Math.PI / 180);

const RIGHT_ANGLE_COLOR = hexToRgbArray(canvasColors.lineBlue);

type MercXY = { x: number; y: number };

/** Lng/lat pair for open path or preview point */
type LngLat = [number, number];

/**
 * Detect whether the angle at vertex B (formed by edges A→B and B→C) is 90°.
 * Operates in Mercator space which is locally conformal (angle-preserving),
 * unlike raw lng/lat where longitude is compressed by cos(lat).
 * @returns true when the angle is within {@link RIGHT_ANGLE_TOLERANCE} of π/2
 */
function isRightAngleMercator(a: MercXY, b: MercXY, c: MercXY): boolean {
  const bax = a.x - b.x;
  const bay = a.y - b.y;
  const bcx = c.x - b.x;
  const bcy = c.y - b.y;

  const dot = bax * bcx + bay * bcy;
  const magBA = Math.sqrt(bax * bax + bay * bay);
  const magBC = Math.sqrt(bcx * bcx + bcy * bcy);
  if (magBA < 1e-15 || magBC < 1e-15) return false;

  const cosAngle = dot / (magBA * magBC);
  const angle = Math.acos(Math.min(1, Math.max(-1, cosAngle)));
  return Math.abs(angle - Math.PI / 2) < RIGHT_ANGLE_TOLERANCE;
}

/**
 * Convert a CSS pixel size to Mercator coordinate units at a given zoom level.
 * Mapbox tile size is 512px, so 1 px = 1 / (512 * 2^zoom) Mercator units.
 */
function pxToMercator(px: number, zoom: number): number {
  return px / (512 * Math.pow(2, zoom));
}

type Vec3 = { x: number; y: number; z: number };

/**
 * Push a solid thin line segment between two points in floating-origin Mercator space.
 */
function pushSolidLine(
  vertices: PointData[],
  start: Vec3,
  end: Vec3,
  color: [number, number, number],
  lineWidthScale: number,
): void {
  const [r, g, b] = color;

  const addVertex = (curr: Vec3, other: Vec3, side: number, along: number) => {
    vertices.push({
      x: curr.x,
      y: curr.y,
      z: curr.z,
      ox: other.x,
      oy: other.y,
      oz: other.z,
      side,
      along,
      r,
      g,
      b,
      opacity: 1,
      distance: 0,
      isDashed: false,
      lineWidthScale,
    });
  };

  addVertex(start, end, 1, -1);
  addVertex(start, end, -1, -1);
  addVertex(end, start, -1, 1);

  addVertex(end, start, -1, 1);
  addVertex(end, start, 1, 1);
  addVertex(start, end, 1, -1);
}

/**
 * Push vertices for one ∟ right-angle indicator at corner c (between edges p→c and c→n).
 * No-op if the angle at c is not within tolerance of 90°.
 */
function pushRightAngleAtCorner(
  vertices: PointData[],
  pMerc: MercXY & { z: number },
  cMerc: MercXY & { z: number },
  nMerc: MercXY & { z: number },
  originMercator: mapboxgl.MercatorCoordinate,
  zoom: number,
): void {
  if (!isRightAngleMercator(pMerc, cMerc, nMerc)) return;

  // Do not apply line-width zoom boost — that made ∟ marks grow when zooming out.
  const armMerc = pxToMercator(ARM_SIZE_PX, zoom);

  const toPrevDx = pMerc.x - cMerc.x;
  const toPrevDy = pMerc.y - cMerc.y;
  const toPrevLen = Math.sqrt(toPrevDx * toPrevDx + toPrevDy * toPrevDy);

  const toNextDx = nMerc.x - cMerc.x;
  const toNextDy = nMerc.y - cMerc.y;
  const toNextLen = Math.sqrt(toNextDx * toNextDx + toNextDy * toNextDy);

  if (toPrevLen < 1e-15 || toNextLen < 1e-15) return;

  const upx = toPrevDx / toPrevLen;
  const upy = toPrevDy / toPrevLen;
  const unx = toNextDx / toNextLen;
  const uny = toNextDy / toNextLen;

  const cx = cMerc.x - originMercator.x;
  const cy = cMerc.y - originMercator.y;
  const cz = cMerc.z - originMercator.z;

  const armOnPrev: Vec3 = {
    x: cx + upx * armMerc,
    y: cy + upy * armMerc,
    z: cz,
  };
  const armOnNext: Vec3 = {
    x: cx + unx * armMerc,
    y: cy + uny * armMerc,
    z: cz,
  };
  const elbow: Vec3 = {
    x: cx + (upx + unx) * armMerc,
    y: cy + (upy + uny) * armMerc,
    z: cz,
  };

  const lineScale = mapRenderingConstants.lineWidthScaleVertical;
  pushSolidLine(vertices, armOnPrev, elbow, RIGHT_ANGLE_COLOR, lineScale);
  pushSolidLine(vertices, elbow, armOnNext, RIGHT_ANGLE_COLOR, lineScale);
}

/**
 * Build right-angle indicator vertices for all 90° corners of a shape.
 * Each indicator is two short perpendicular line segments forming an ∟ symbol
 * inset from the corner along both edges, sized at a constant {@link ARM_SIZE_PX}
 * pixels regardless of zoom.
 *
 * @param shape Shape whose corners to inspect
 * @param origin Geographic origin for metric↔coordinate conversion
 * @param elevation Storey base elevation in meters
 * @param originMercator Floating origin for Mercator calculations
 * @param zoom Current map zoom level for screen-space sizing
 */
export function buildRightAngleVertices({
  shape,
  origin,
  elevation,
  originMercator,
  zoom,
}: {
  shape: Shape;
  origin: Coordinate;
  elevation: number;
  originMercator: mapboxgl.MercatorCoordinate;
  zoom: number;
}): PointData[] {
  if (!shape.points || shape.points.length < 3) return [];

  const ring = getShapeRingCoordinates(shape, origin);
  if (ring.length < 4) return [];

  // Open ring (last === first in closed ring, so we skip the duplicate)
  const count = ring.length - 1;
  const vertices: PointData[] = [];

  // Pre-convert all ring points to Mercator for accurate angle detection
  const mercRing = ring.map(([lng, lat]) =>
    mapboxgl.MercatorCoordinate.fromLngLat({ lng, lat }, elevation),
  );

  for (let i = 0; i < count; i++) {
    const pMerc = mercRing[(i - 1 + count) % count]!;
    const cMerc = mercRing[i]!;
    const nMerc = mercRing[(i + 1) % count]!;
    pushRightAngleAtCorner(vertices, pMerc, cMerc, nMerc, originMercator, zoom);
  }

  return vertices;
}

/**
 * Build right-angle indicator vertices for an open draft path (and optionally at the
 * last corner when a preview point is provided). Used while drawing a draft shape
 * so 90° corners show the ∟ symbol, including the corner formed by the preview line.
 *
 * @param path Open path as lng/lat pairs (not closed)
 * @param elevation Elevation in meters for Mercator conversion
 * @param originMercator Floating origin for Mercator calculations
 * @param zoom Current map zoom level for screen-space sizing
 * @param previewPoint Optional lng/lat of crosshair; when set, angle at last vertex (last → preview) is checked
 */
export function buildRightAngleVerticesForOpenPath({
  path,
  elevation,
  originMercator,
  zoom,
  previewPoint,
}: {
  path: LngLat[];
  elevation: number;
  originMercator: mapboxgl.MercatorCoordinate;
  zoom: number;
  previewPoint?: LngLat;
}): PointData[] {
  // Need at least 2 points for preview corner, or 3 for any internal corner
  const hasInternalCorners = path.length >= 3;
  const hasPreviewCorner = path.length >= 2 && previewPoint != null;
  if (!hasInternalCorners && !hasPreviewCorner) return [];

  const vertices: PointData[] = [];
  const mercPath = path.map(([lng, lat]) =>
    mapboxgl.MercatorCoordinate.fromLngLat({ lng, lat }, elevation),
  );

  // Internal corners: indices 1 .. path.length - 2
  for (let i = 1; i < path.length - 1; i += 1) {
    const pMerc = mercPath[i - 1]!;
    const cMerc = mercPath[i]!;
    const nMerc = mercPath[i + 1]!;
    pushRightAngleAtCorner(vertices, pMerc, cMerc, nMerc, originMercator, zoom);
  }

  // Last corner with preview line (second-to-last → last → preview)
  if (previewPoint && path.length >= 2) {
    const prevMerc = mercPath[path.length - 2]!;
    const lastMerc = mercPath[path.length - 1]!;
    const previewMerc = mapboxgl.MercatorCoordinate.fromLngLat(
      { lng: previewPoint[0], lat: previewPoint[1] },
      elevation,
    );
    pushRightAngleAtCorner(
      vertices,
      prevMerc,
      lastMerc,
      previewMerc,
      originMercator,
      zoom,
    );
  }

  return vertices;
}
